/**
 * Simple helper to track SSE activity for DevRenderOverlay.
 * - notifySseActivity(eventName) should be called when an SSE event arrives.
 * - subscribe(callback) allows components to react to activity state changes.
 */

const ACTIVITY_MS = 1500; // how long to keep "active" state after last event
let active = false;
let lastEvent = null;
let lastTimestamp = 0;
const counts = new Map();
let clearTimer = null;
const listeners = new Set();

const notifyListeners = () => {
  const snapshot = {
    active,
    lastEvent,
    lastTimestamp,
    counts: Object.fromEntries(counts),
  };

  listeners.forEach((cb) => {
    try {
      cb(snapshot);
    } catch (e) {
      // ignore listener errors
    }
  });
};

export function notifySseActivity(eventName = 'sse') {
  lastEvent = eventName;
  lastTimestamp = Date.now();
  counts.set(eventName, (counts.get(eventName) || 0) + 1);

  if (!active) {
    active = true;
  }

  if (clearTimer) {
    clearTimeout(clearTimer);
  }

  clearTimer = setTimeout(() => {
    active = false;
    clearTimer = null;
    notifyListeners();
  }, ACTIVITY_MS);

  notifyListeners();
}

export function isActive() {
  return active;
}

export function getLastActivity() {
  return { event: lastEvent, ts: lastTimestamp };
}

export function getCounts() {
  return Object.fromEntries(counts);
}

export function resetSseActivity() {
  active = false;
  lastEvent = null;
  lastTimestamp = 0;
  counts.clear();
  if (clearTimer) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
  notifyListeners();
}

/**
 * Subscribe to activity snapshots. Callback receives { active, lastEvent, lastTimestamp, counts }.
 * Returns an unsubscribe function.
 */
export function subscribe(cb) {
  listeners.add(cb);
  // call immediately with current state
  try {
    cb({ active, lastEvent, lastTimestamp, counts: Object.fromEntries(counts) });
  } catch (e) {}
  return () => listeners.delete(cb);
}

export default {
  notifySseActivity,
  isActive,
  getLastActivity,
  getCounts,
  resetSseActivity,
  subscribe,
};
